import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, RefreshCw, Search, Sparkles, Wrench, X } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { SkeletonList } from '../components/ui/Skeleton'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { SkillService, type Skill, type SkillBoardCard } from '../services/skillService'
import { describeTaskCleanup, lifeAreaLabel } from '../utils/taskForm'
import { groupSkills, matchesQuery, summarize, summaryWords, type SkillGroupBy, type SkillSortBy, type SortDir } from '../utils/skillSections'
import { SkillRow } from '../components/skills/SkillRow'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'

/**
 * The skill list (Skills UX pass 2026-09-06), read the way a person reads it
 * — which skills am I building, which one is ready for my practice next, and
 * how far along is each:
 *
 *   - Logo, one title, one summary line, two verbs — the task list's frame.
 *   - A quick-add row: a name is enough; it lands under "Needs a first step".
 *   - Sections by movement: Ready to check · In motion · Needs a first step ·
 *     Waiting for you · Paused · Good enough for now (collapsed). Goal / Life
 *     area / None one click away.
 *   - A row is the ladder, a title, one quiet line: the next thing to
 *     practise, what got done lately. Nothing is marked on a skill itself —
 *     the marks live on the tasks and habits under it. The rest under ⋯.
 *   - Search instead of pagination; group, sort and direction remembered.
 */
const GROUP_PREF = 'skills.groupBy'
const SORT_PREF = 'skills.sortBy'
const DIR_PREF = 'skills.sortDir'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return (allowed as readonly string[]).includes(v || '') ? (v as T) : fallback
  } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* per-viewer convenience only */ }
}

export const Skills: React.FC = () => {
  const { isDiary: diaryMode } = useTheme()
  const { assistant: personaName } = useBranding()
  const { timezone } = useUserTimezone()
  const navigate = useNavigate()
  const toast = useToast()
  const [skills, setSkills] = useState<Skill[]>([])
  const [cards, setCards] = useState<Record<string, SkillBoardCard | undefined>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [boardIssue, setBoardIssue] = useState(false)
  const [query, setQuery] = useState('')
  const [groupBy, setGroupByState] = useState<SkillGroupBy>(() => readPref(GROUP_PREF, ['movement', 'goal', 'area', 'none'] as const, 'movement'))
  const [sortBy, setSortByState] = useState<SkillSortBy>(() => readPref(SORT_PREF, ['attention', 'level', 'created', 'updated'] as const, 'attention'))
  const [sortDir, setSortDirState] = useState<SortDir>(() => readPref(DIR_PREF, ['asc', 'desc'] as const, 'desc'))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [deleteTarget, setDeleteTarget] = useState<Skill | null>(null)
  const [today, setToday] = useState(todayKey)
  const skillService = useMemo(() => new SkillService(), [])

  useProvideFeedbackContext({ pageType: 'skills', extra: { groupBy, sortBy, sortDir, searching: Boolean(query) } })

  const setGroupBy = (g: SkillGroupBy) => { setGroupByState(g); writePref(GROUP_PREF, g) }
  const setSortBy = (s: SkillSortBy) => { setSortByState(s); writePref(SORT_PREF, s) }
  const setSortDir = (d: SortDir) => { setSortDirState(d); writePref(DIR_PREF, d) }

  const load = useCallback(async () => {
    const day = todayKey()
    setToday(day)
    try {
      setError(null)
      // include=all: rationing parks fresh skills; an active-only read once
      // rendered this page empty while the life map showed the same items.
      const [rows, board] = await Promise.all([
        skillService.getSkills(true),
        skillService.getBoard(day).then((b) => { setBoardIssue(false); return b }).catch(() => { setBoardIssue(true); return null }),
      ])
      setSkills(Array.isArray(rows) ? rows : [])
      setCards(board?.skills || {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills')
    } finally {
      setLoading(false)
    }
  }, [skillService])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => skills.filter((s) => matchesQuery(s, query)), [skills, query])
  const groups = useMemo(() => groupSkills(visible, cards, groupBy, today, lifeAreaLabel, { by: sortBy, dir: sortDir }), [visible, cards, groupBy, today, sortBy, sortDir])
  const summary = useMemo(() => summarize(skills, cards, today), [skills, cards, today])

  // ── verbs ──
  const confirmDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    try {
      const result = await skillService.deleteSkill(String(target.id))
      setDeleteTarget(null)
      await load()
      toast.toast({ variant: 'success', title: 'Skill deleted', description: describeTaskCleanup(result.cleanup) || undefined })
    } catch (err) {
      setDeleteTarget(null)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const quickAdd = async (title: string) => {
    const created = await skillService.createSkill({ title, priority: 'medium' })
    await load()
    toast.toast({ variant: 'success', title: 'Added', description: title, action: created?.id ? { label: 'Plan the journey', onClick: () => navigate(`/skills/${created.id}`) } : undefined })
  }

  const toggle = (key: string, def: boolean) => setCollapsed((c) => ({ ...c, [key]: !(c[key] ?? def) }))

  // ── frame ──
  if (loading) return <div className="space-y-4" aria-busy="true" aria-label="Loading skills"><SkeletonList count={5} /></div>
  if (error && skills.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">The list would not load</h2>
          <p className="mb-4 text-secondary">{error}</p>
          <Button onClick={() => { setLoading(true); void load() }}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>
      </div>
    )
  }

  const empty = skills.length === 0
  const nothingMatches = !empty && visible.length === 0

  return (
    <div className="space-y-4" data-testid="skills-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand sm:h-10 sm:w-10" aria-hidden="true" data-testid="skills-logo">
            <Wrench className="h-5 w-5 text-on-brand" />
          </span>
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold leading-tight text-primary sm:text-2xl ${diaryMode ? 'font-handwritten' : ''}`}>Skills</h1>
            <p className="text-app-meta text-secondary" data-testid="skills-summary">{summaryWords(summary)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" onClick={() => navigate('/skills/create')} data-testid="skills-new"><Plus className="mr-1 h-4 w-4" />New skill</Button>
          <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=skills&type=skill')} data-testid="skills-ask"><Sparkles className="mr-1 h-4 w-4" />{personaName}</Button>
        </div>
      </div>

      {boardIssue && !empty && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tint-bad-edge bg-tint-bad-soft p-3 text-bad" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-app-meta">We could not load the paths and practice, so the rows show the skills only.</span>
          <Button onClick={() => void load()} variant="outline" size="xs" className="ml-auto shrink-0"><RefreshCw className="mr-1.5 h-4 w-4" />Try again</Button>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="relative block w-full sm:w-64 lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a skill" aria-label="Find a skill"
                   className="min-h-touch w-full rounded-md border border-soft bg-surface pl-8 pr-8 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5" data-testid="skills-search" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-primary"><X className="h-3.5 w-3.5" /></button>}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-app-micro">
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Group by</span>
              <div role="radiogroup" aria-label="Group by" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['movement', 'Movement'], ['goal', 'Goal'], ['area', 'Life area'], ['none', 'None']] as Array<[SkillGroupBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={groupBy === k} onClick={() => setGroupBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${groupBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`skills-group-${k}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Sort</span>
              <div role="radiogroup" aria-label="Sort" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['attention', 'Attention'], ['level', 'Level'], ['created', 'Created'], ['updated', 'Updated']] as Array<[SkillSortBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={sortBy === k} onClick={() => setSortBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${sortBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`skills-sort-${k}`}>{label}</button>
                ))}
              </div>
              {sortBy !== 'attention' && (
                <button type="button" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')} aria-label={sortDir === 'desc' ? 'Highest first. Switch to lowest first' : 'Lowest first. Switch to highest first'}
                        className="inline-flex items-center gap-0.5 rounded-md border border-soft bg-surface px-1.5 py-1 font-medium text-secondary hover:text-primary" data-testid="skills-sort-dir">
                  {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                  {sortBy === 'level' ? (sortDir === 'desc' ? 'Furthest along' : 'Just starting') : (sortDir === 'desc' ? 'Newest' : 'Oldest')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskQuickAdd onAdd={quickAdd} onMore={(title) => navigate('/skills/create', { state: title ? { title } : undefined })} autoFocus={empty} placeholder="Add a skill — a name is enough" />

      {empty && (
        <div className="rounded-xl border border-soft bg-surface p-6 text-center" data-testid="skills-empty">
          <p className="text-app-body font-semibold text-primary">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-app-meta text-secondary">
            Put one thing you want to get better at in the box above — a name is enough. Where you are and where you want to get can come later.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=skills&type=skill')}><Sparkles className="mr-1 h-4 w-4" />Build with {personaName}</Button>
          </div>
        </div>
      )}

      {nothingMatches && (
        <p className="text-app-meta text-muted" data-testid="skills-no-match">Nothing matches “{query}”. <button type="button" onClick={() => setQuery('')} className="text-brand hover:underline">Clear the search</button></p>
      )}

      {!empty && !nothingMatches && groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? Boolean(g.collapsedByDefault)
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        return (
          <section key={g.key} data-testid={`skills-section-${g.key}`} aria-labelledby={`skills-h-${g.key}`}>
            <button type="button" onClick={() => toggle(g.key, Boolean(g.collapsedByDefault))} aria-expanded={!isCollapsed} className="flex w-full items-center gap-1.5 rounded py-1 text-left hover:text-brand" data-testid="skills-section-toggle">
              <Chevron className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
              <h2 id={`skills-h-${g.key}`} className="text-app-meta font-bold uppercase tracking-widest text-secondary">{g.title}</h2>
              <span className="text-app-micro text-muted" data-testid="skills-section-count">{g.items.length}</span>
            </button>
            {!isCollapsed && g.items.length > 0 && (
              <div className="mt-1 space-y-1.5">
                {g.items.map((s) => (
                  <SkillRow key={s.id} skill={s} card={cards[String(s.id)]} showParent={groupBy !== 'goal'} showArea={groupBy === 'goal'} tz={timezone}
                            timeHint={sortBy === 'created' || sortBy === 'updated' ? sortBy : null} onDelete={setDeleteTarget} diaryMode={diaryMode} />
                ))}
              </div>
            )}
            {!isCollapsed && g.items.length === 0 && g.quiet && <p className="mt-0.5 pl-5 text-app-meta text-muted" data-testid="skills-section-quiet">{g.quiet}</p>}
          </section>
        )
      })}

      <ConfirmationModal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} title="Delete this skill?"
                         message={`“${deleteTarget?.title}” will be removed, along with the path steps not yet started. Tasks and habits you made under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default Skills
